import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import Typography from '@material-ui/core/Typography'
import BootstrapTable from 'react-bootstrap-table-next'
import filterFactory from 'react-bootstrap-table2-filter'
import AddDeliveryModal from 'components/admin/AddDeliveryModal'
import DeliveryModal from 'components/admin/DeliveryModal'
import { getProductString } from 'config/helpers/Helpers'

import componentStyles from 'assets/theme/views/admin/react-bs-table.js'

const useStyles = makeStyles(componentStyles)

const deliveryTypeBadge = (type) => {
    const styles = {
        weekly: { backgroundColor: '#5e72e4', label: 'Weekly' },
        balance: { backgroundColor: '#2dce89', label: 'Balance' },
        hidden: { backgroundColor: '#8898aa', label: 'Hidden' },
    }
    const s = styles[type] || styles.hidden
    return (
        <span style={{
            fontSize: '10px', fontWeight: 700, color: '#fff',
            backgroundColor: s.backgroundColor, padding: '2px 6px',
            borderRadius: '3px', textTransform: 'uppercase'
        }}>
            {s.label}
        </span>
    )
}

const deliveryMethodBadge = (method) => {
    const styles = {
        bd: { backgroundColor: '#11cdef', label: 'BD' },
        webhook: { backgroundColor: '#fb6340', label: 'Webhook' },
        email: { backgroundColor: '#f5365c', label: 'Email' },
    }
    const s = styles[method] || styles.bd
    return (
        <span style={{
            fontSize: '10px', fontWeight: 700, color: '#fff',
            backgroundColor: s.backgroundColor, padding: '2px 6px',
            borderRadius: '3px', textTransform: 'uppercase', marginLeft: '4px'
        }}>
            {s.label}
        </span>
    )
}

function FSDeliveriesTable(props) {
    const classes = { ...useStyles() }
    const tableType = props.tableType || 'weekly' // weekly | balance | unlimited
    const isThisWeek = props.period === 'thisWeek'

    const [deliveries, setDeliveries] = useState(props.deliveries)
    const [delivery, setDelivery] = useState(null)
    const [showDeliveryModal, setShowDeliveryModal] = useState(false)
    const [showAddDeliveryModal, setShowAddDeliveryModal] = useState(false)

    useEffect(() => {
        setDeliveries(props.deliveries)
    }, [props.deliveries])

    // --- Shared column builders ---
    const companyCol = {
        dataField: 'companyName',
        text: 'Company',
        sort: true,
        editable: false,
        formatter: (cell, row) => {
            const isClickable = tableType === 'weekly' && !row.isReadOnly && !row.isStartPension
            return (
                <Box
                    className={row.active === false ? 'inactive-delivery' : 'active-delivery'}
                    onClick={isClickable ? () => openDeliveryModal(row) : undefined}
                    style={{ cursor: isClickable ? 'pointer' : 'default' }}
                >
                    <span className={isClickable ? 'hover-text primary-text' : ''}>
                        {row.companyName || row.companyId}
                    </span>
                    {row.isStartPension && (
                        <span style={{
                            fontSize: '10px', fontWeight: 700, color: '#fff',
                            backgroundColor: '#8965e0', padding: '2px 6px',
                            borderRadius: '3px', textTransform: 'uppercase', marginLeft: '6px'
                        }}>
                            Start Pensions
                        </span>
                    )}
                    {row.priority === true && (
                        <span style={{
                            fontSize: '10px', fontWeight: 700, color: '#fff',
                            backgroundColor: '#f5365c', padding: '2px 6px',
                            borderRadius: '3px', textTransform: 'uppercase', marginLeft: '6px'
                        }}>
                            Priority
                        </span>
                    )}
                    {row.note && !row.isStartPension && (
                        <Box component='span' style={{ fontSize: '11px', color: '#8898aa', marginLeft: '6px' }}>
                            {row.note}
                        </Box>
                    )}
                </Box>
            )
        },
        footer: 'Total'
    }

    const productCol = {
        dataField: 'product',
        text: 'Product',
        sort: true,
        editable: false,
        formatter: (cell, row) => {
            return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>{getProductString(row.product)}</Box>
        },
        footer: ''
    }

    const methodCol = {
        dataField: 'deliveryMethod',
        text: 'Method',
        sort: true,
        editable: false,
        formatter: (cell, row) => {
            return (
                <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                    {deliveryMethodBadge(row.deliveryMethod)}
                </Box>
            )
        },
        footer: ''
    }

    const leadPriceCol = {
        dataField: 'leadPrice',
        text: 'Lead Price',
        sort: true,
        editable: false,
        formatter: (cell, row) => {
            // Variable-priced weekly delivery: the flat leadPrice is not what's billed —
            // show a badge instead, with the band prices in the hover tooltip
            if (row.usePricingTable && row.pricing) {
                const tooltip = Object.entries(row.pricing).map(([band, price]) => `${band}: €${price}`).join('\n')
                    + `\n\nNote: expected revenue is estimated at the flat lead price (€${parseFloat(row.leadPrice || 0).toFixed(2)} × allocation) — actual billing is per band.`
                return (
                    <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                        <span title={tooltip} style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '10px', fontWeight: 700, color: '#fff', lineHeight: 1,
                            backgroundColor: '#8965e0',
                            // uppercase text reserves descender space it never uses, so the bottom
                            // padding is trimmed to sit the caps optically centred; the table's
                            // inherited letter-spacing is reset as it offsets the label horizontally
                            padding: '5px 7px 4px', letterSpacing: 0,
                            borderRadius: '3px', textTransform: 'uppercase', cursor: 'help'
                        }}>
                            Variable
                        </span>
                    </Box>
                )
            }
            if (row.leadPrice == null) {
                return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'} style={{ color: '#8898aa' }}>—</Box>
            }
            return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>€{parseFloat(row.leadPrice).toFixed(2)}</Box>
        },
        footer: ''
    }

    const leadsCol = {
        dataField: 'leadCount',
        text: 'Leads',
        sort: true,
        editable: false,
        formatter: (cell, row) => {
            return (
                <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                    {row.leadCount || 0}
                    {row.hvCount > 0 && (
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#8898aa', marginLeft: '4px' }}>
                            ({row.hvCount} HV)
                        </span>
                    )}
                </Box>
            )
        },
        footer: deliveries.reduce((total, d) => total + (parseInt(d.leadCount) || 0), 0).toString()
    }

    const revenueCol = {
        dataField: 'revenue',
        text: 'Revenue',
        sort: true,
        editable: false,
        formatter: (cell, row) => {
            return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>€{row.revenue ? row.revenue.toFixed(2) : '0.00'}</Box>
        },
        footer: '€' + deliveries.reduce((total, d) => total + (parseFloat(d.revenue) || 0), 0).toFixed(2)
    }

    // --- Type-specific columns ---
    const weeklyColumns = [
        companyCol,
        productCol,
        {
            dataField: 'allocation',
            text: 'Allocation',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                const total = (parseInt(row.allocation) || 0) + (parseInt(row.extraAllocation) || 0)
                const extra = parseInt(row.extraAllocation) || 0
                return (
                    <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                        {total}
                        {extra > 0 && <span style={{ fontSize: '11px', color: '#2dce89', marginLeft: '4px' }}>(+{extra})</span>}
                    </Box>
                )
            },
            footer: (() => {
                const total = deliveries
                    .reduce((sum, d) => sum + (parseInt(d.allocation) || 0) + (parseInt(d.extraAllocation) || 0), 0)
                return String(total)
            })()
        },
        leadPriceCol,
        leadsCol,
        revenueCol,
        ...(isThisWeek ? [{
            dataField: 'fillRate',
            text: 'Fill %',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                const cap = (parseInt(row.allocation) || 0) + (parseInt(row.extraAllocation) || 0)
                const fill = cap > 0 ? Math.round((row.leadCount || 0) / cap * 100) : 0
                let color = '#2dce89'
                if (fill >= 90) color = '#f5365c'
                else if (fill >= 70) color = '#fb6340'
                else if (fill >= 50) color = '#ffd600'

                return (
                    <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                        <Box display='inline-flex' alignItems='center' style={{ gap: '6px' }}>
                            <Box style={{
                                width: '50px', height: '8px', borderRadius: '4px',
                                backgroundColor: '#e9ecef', overflow: 'hidden'
                            }}>
                                <Box style={{
                                    width: `${Math.min(fill, 100)}%`, height: '100%',
                                    borderRadius: '4px', backgroundColor: color
                                }} />
                            </Box>
                            <span>{fill}%</span>
                        </Box>
                    </Box>
                )
            },
            footer: ''
        },
        {
            dataField: 'remaining',
            text: 'Remaining',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                const cap = (parseInt(row.allocation) || 0) + (parseInt(row.extraAllocation) || 0)
                const remaining = cap - (row.leadCount || 0)
                const color = remaining <= 0 ? '#f5365c' : remaining <= 3 ? '#fb6340' : undefined
                return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'} style={{ color, fontWeight: color ? 600 : undefined }}>{remaining}</Box>
            },
            footer: (() => {
                const total = deliveries
                    .reduce((sum, d) => {
                        const cap = (parseInt(d.allocation) || 0) + (parseInt(d.extraAllocation) || 0)
                        return sum + (cap - (d.leadCount || 0))
                    }, 0)
                return String(total)
            })()
        }] : [])
    ]

    const balanceColumns = [
        companyCol,
        productCol,
        {
            dataField: 'balance',
            text: 'Balance',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                const bal = row.balance != null ? parseFloat(row.balance) : 0
                const balColor = bal > 0 ? '#2dce89' : bal === 0 ? '#8898aa' : '#f5365c'
                return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'} style={{ color: balColor, fontWeight: 600 }}>€{bal.toFixed(2)}</Box>
            },
            footer: '€' + deliveries.reduce((sum, d) => sum + (parseFloat(d.balance) || 0), 0).toFixed(2)
        },
        leadsCol
    ]

    const unlimitedColumns = [
        { ...companyCol, formatter: (cell, row) => {
            return (
                <Box className='active-delivery'>
                    <span>{row.companyName || row.companyId}</span>
                    {row.isStartPension && (
                        <span style={{
                            fontSize: '10px', fontWeight: 700, color: '#fff',
                            backgroundColor: '#8965e0', padding: '2px 6px',
                            borderRadius: '3px', textTransform: 'uppercase', marginLeft: '6px'
                        }}>
                            Start Pensions
                        </span>
                    )}
                    {row.note && !row.isStartPension && (
                        <Box component='span' style={{ fontSize: '11px', color: '#8898aa', marginLeft: '6px' }}>
                            {row.note}
                        </Box>
                    )}
                </Box>
            )
        }},
        productCol,
        {
            dataField: 'billingFrequency',
            text: 'Billing',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                const styles = {
                    weekly: { backgroundColor: '#5e72e4', label: 'Weekly' },
                    monthly: { backgroundColor: '#2dce89', label: 'Monthly' },
                    quarterly: { backgroundColor: '#fb6340', label: 'Quarterly' },
                }
                const s = styles[row.billingFrequency] || styles.weekly
                return (
                    <span style={{
                        fontSize: '10px', fontWeight: 700, color: '#fff',
                        backgroundColor: s.backgroundColor, padding: '2px 6px',
                        borderRadius: '3px', textTransform: 'uppercase'
                    }}>
                        {s.label}
                    </span>
                )
            },
            footer: ''
        },
        leadPriceCol,
        leadsCol,
        revenueCol
    ]

    const columns = tableType === 'weekly' ? weeklyColumns
        : tableType === 'balance' ? balanceColumns
        : unlimitedColumns

    function openDeliveryModal(delivery) {
        if (delivery.isReadOnly) return
        setDelivery(delivery)
        setShowDeliveryModal(true)
    }

    function closeDeliveryModal() {
        setShowDeliveryModal(false)
        setDelivery(null)
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <Box display='flex' style={{ justifyContent: 'space-between' }}>
                        <Box>
                            <Box
                                component={Typography}
                                variant='h2'
                                marginBottom='0!important'
                            >
                                <Box component='span'>{props.title}</Box>
                            </Box>
                        </Box>
                        {tableType === 'weekly' && (
                            <Box>
                                <Button
                                    component={Box}
                                    onClick={() => setShowAddDeliveryModal(true)}
                                    color='primary'
                                    variant='contained'
                                    size='small'
                                >
                                    Add Delivery
                                </Button>
                            </Box>
                        )}
                    </Box>
                }
                classes={{ root: classes.cardHeaderRoot }}
            />

            <div className='table-container'>
                <BootstrapTable
                    keyField='compositeKey'
                    data={deliveries}
                    columns={columns}
                    filter={filterFactory()}
                    noDataIndication={<Box>No deliveries</Box>}
                />
            </div>

            {props.footerNote &&
                <Box style={{ padding: '0 1.5rem 1.25rem', fontSize: '12px', color: '#8898aa' }}>
                    {props.footerNote}
                </Box>
            }

            {showAddDeliveryModal &&
                <AddDeliveryModal
                    isOpen={showAddDeliveryModal}
                    close={() => setShowAddDeliveryModal(false)}
                    showSuccessAlert={(text) => props.showSuccessAlert(text)}
                    refreshDeliveries={() => props.refreshDeliveries()}
                    companies={props.companies || []}
                    campaigns={props.campaigns || []}
                />
            }

            {showDeliveryModal && delivery && delivery.deliveryType === 'weekly' &&
                <DeliveryModal
                    isOpen={showDeliveryModal}
                    close={() => closeDeliveryModal()}
                    delivery={delivery}
                    updateDelivery={(d) => props.updateDelivery(d)}
                    showSuccessAlert={(text) => props.showSuccessAlert(text)}
                    companies={props.companies || []}
                    campaigns={props.campaigns || []}
                />
            }
        </Card>
    )
}

export default FSDeliveriesTable
